import * as React from "react";
import SignupLayout from "../SignupLayout";
import BusinessDetail from "./BusinessDetail";
import SignupSummary from "./SignupSummary";
import { sellerSignUpInputSchema, ISellerSignUpInput } from "muminsmart-utils";
import SubmittedCard from "./SubmittedCard";
import { routes } from "@/lib/constants";
import { useNavigate } from "react-router-dom";
import { Stepper } from "../../../common/Stepper";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { sellerSignup } from "@/lib/api/auth-actions";
import { toast } from "sonner";
import {
  OtpVerificationState,
  normalizePhoneNumber,
  normalizeEmail,
} from "../ContactOtpVerification";
import SellerTermsAndConditionsDialog from "./SellerTermsAndConditionsDialog";
import SellerPrivacyPolicyDialog from "./SellerPrivacyPolicyDialog";

const SellerSignupForm: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [hasAcceptedPolicies, setHasAcceptedPolicies] = React.useState(false);
  const [consentError, setConsentError] = React.useState<string | null>(null);
  const [phoneVerification, setPhoneVerification] =
    React.useState<OtpVerificationState>({
      isVerified: false,
      verifiedOtp: null,
      value: "",
    });
  const [emailVerification, setEmailVerification] =
    React.useState<OtpVerificationState>({
      isVerified: false,
      verifiedOtp: null,
      value: "",
    });

  const form = useForm<ISellerSignUpInput>({
    resolver: zodResolver(sellerSignUpInputSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "+91",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
      authorizedSignatory: "",
      password: "",
      shippingCharge: 0,
      transportCharge: 0,
    },
    mode: "onChange",
  });

  const steps = [
    {
      title: "Business Details",
      component: (
        <BusinessDetail
          key="business-detail"
          onPhoneVerificationChange={setPhoneVerification}
          onEmailVerificationChange={setEmailVerification}
        />
      ),
    },
    {
      title: "Summary",
      component: <SignupSummary key="signup-summary" />,
    },
  ];

  const next = async () => {
    let valid = false;
    switch (currentStep) {
      case 0:
        valid = await form.trigger([
          "name",
          "phoneNumber",
          "email",
          "password",
          "addressLine1",
          "addressLine2",
          "country",
          "state",
          "city",
          "postalCode",
          "gstNumber",
          "gumastaLicense",
          "authorizedSignatory",
          "cinNumber",
          "panNumber",
          "shippingCharge",
          "transportCharge",
        ]);
        if (valid) {
          const normalizedPhoneNumber = normalizePhoneNumber(
            form.getValues("phoneNumber") || "",
          );
          const normalizedEmail = normalizeEmail(form.getValues("email") || "");

          if (
            !normalizedPhoneNumber ||
            !phoneVerification.isVerified ||
            phoneVerification.value !== normalizedPhoneNumber
          ) {
            toast.error("Please verify your phone number before continuing.");
            valid = false;
          }

          if (
            !normalizedEmail ||
            !emailVerification.isVerified ||
            emailVerification.value !== normalizedEmail
          ) {
            toast.error("Please verify your email before continuing.");
            valid = false;
          }
        }
        break;
      case 1:
        // No required fields in variants step for now
        valid = await form.trigger([]);
        break;
      default:
        break;
    }

    if (valid) setCurrentStep((s) => s + 1);
  };

  const prev = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    } else {
      return navigate(-1);
    }
  };

  const onSubmit = async (data: ISellerSignUpInput) => {
    const normalizedPhoneNumber = normalizePhoneNumber(data.phoneNumber || "");
    const normalizedEmail = normalizeEmail(data.email || "");

    if (
      !normalizedPhoneNumber ||
      !phoneVerification.isVerified ||
      phoneVerification.value !== normalizedPhoneNumber
    ) {
      toast.error("Please verify your phone number before submitting.");
      return;
    }

    if (
      !normalizedEmail ||
      !emailVerification.isVerified ||
      emailVerification.value !== normalizedEmail
    ) {
      toast.error("Please verify your email before submitting.");
      return;
    }

    if (!emailVerification.verifiedOtp) {
      toast.error("Email verification OTP is missing. Please verify again.");
      return;
    }

    if (!hasAcceptedPolicies) {
      setConsentError(
        "Please accept the Terms and Conditions and Privacy Policy.",
      );
      toast.error("Please accept the Terms and Conditions and Privacy Policy.");
      return;
    }

    setLoading(true);

    try {
      data.phoneNumber = normalizedPhoneNumber;
      data.email = normalizedEmail;
      const res = await sellerSignup(data);
      if (res.success) {
        toast.success(res.message);
        setIsSubmitted(true);
      } else {
        toast.error(res.message || "Registration failed");
      }
    } catch (error: unknown) {
      console.error("Error during registration:", error);
      const message =
        error instanceof Error ? error.message : "An unexpected error occurred";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const preventNativeSubmit: React.FormEventHandler<HTMLFormElement> = (
    event,
  ) => {
    event.preventDefault();
  };

  if (isSubmitted) {
    return (
      <SubmittedCard
        message="Your account request has been submitted, Once we verify details your account will be activated!"
        onClick={() => navigate(routes.home)}
      />
    );
  }

  return (
    <SignupLayout type="seller">
      <div className="px-4">
        <Stepper steps={steps} currentStep={currentStep} />
      </div>
      <div className="p-6 pb-10 mt-4 rounded-t-3xl bg-white">
        <FormProvider {...form}>
          <form onSubmit={preventNativeSubmit} className="space-y-6">
            {steps[currentStep]?.component}

            {currentStep === 0 ? (
              <div className="space-y-2">
                <div className="flex items-start gap-3 rounded-full border border-muted bg-white p-3">
                  <Checkbox
                    id="accept-seller-policies"
                    checked={hasAcceptedPolicies}
                    onCheckedChange={(checked) => {
                      const isChecked = checked === true;
                      setHasAcceptedPolicies(isChecked);
                      if (isChecked) {
                        setConsentError(null);
                      }
                    }}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="accept-seller-policies"
                    className="text-sm text-[#3f3f3f] leading-6"
                  >
                    I agree to the <SellerTermsAndConditionsDialog /> and{" "}
                    <SellerPrivacyPolicyDialog />
                    {"."}
                  </label>
                </div>
                {consentError ? (
                  <p className="text-sm text-destructive">{consentError}</p>
                ) : null}
              </div>
            ) : null}

            <div className="flex mt-6 gap-2">
              <Button
                type="button"
                size="lg"
                className="flex-1 bg-muted rounded-full"
                onClick={prev}
              >
                Back
              </Button>
              {currentStep === steps.length - 1 ? (
                <Button
                  type="button"
                  size="lg"
                  className="flex-1 bg-primary rounded-full"
                  onClick={() => void form.handleSubmit(onSubmit)()}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="lg"
                  className="flex-1 bg-primary rounded-full"
                  onClick={next}
                >
                  Continue
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </SignupLayout>
  );
};

export default SellerSignupForm;
